import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getLayout, getMode, setLayout as persistLayout, setMode as persistMode, applyLayoutAndModeToBody } from '../../utils/theme';
const ThemeContext = /*#__PURE__*/createContext();

/**
 * ThemeProvider manages layout and mode state.
 * Layout controls the shell structure (top vs sidebar).
 * Mode controls the visual theme (classic, light, dark).
 */
export const ThemeProvider = ({
  children
}) => {
  const [layout, setLayoutState] = useState(() => getLayout());
  const [mode, setModeState] = useState(() => getMode());
  useEffect(() => {
    applyLayoutAndModeToBody(layout, mode);
  }, [layout, mode]);
  const setLayout = useCallback(newLayout => {
    if (!persistLayout(newLayout)) {
      return;
    }
    setLayoutState(newLayout);
  }, []);
  const setMode = useCallback(newMode => {
    if (!persistMode(newMode, layout)) {
      return;
    }
    setModeState(newMode);
  }, [layout]);
  const value = useMemo(() => ({
    layout,
    mode,
    setLayout,
    setMode
  }), [layout, mode, setLayout, setMode]);
  return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: value
  }, children);
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Hook to access theme context.
 * @returns {{ layout: string, mode: string, setLayout: Function, setMode: Function }}
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
export default ThemeProvider;